import type {
    AssetId,
    PatchOutlookSpaceAsset,
    ProjectId,
    TimeStreamAsset,
} from 'owa-timestream-types';
import { patchOutlookSpaceAsset } from '../service';
import { toJS } from 'mobx';
import { updateAssetFailed } from './patchAsset.locstring.json';
import { updateCachedAsset } from '../privateActions';
import getAsset from '../selectors/getAsset';
import getAssetOrUndefined from '../selectors/getAssetOrUndefined';
import getStore from '../store';
import loc from 'owa-localize';
import resolveAssetId from '../assetId/resolveAssetId';
import showRetryNotification from './showRetryNotification';

export default async function patchAsset(
    projectId: ProjectId,
    assetId: AssetId,
    changes: PatchOutlookSpaceAsset,
    partialOriginalAsset: Partial<TimeStreamAsset>,
    shouldPatch: (asset: TimeStreamAsset) => boolean
) {
    const { projects } = getStore();

    const assets = projects.get(projectId)?.assets;
    if (!assets) {
        return;
    }

    // Capture the asset instance when the function was invoked - this may be a local lie.
    let assetAtInvocation = toJS(getAssetOrUndefined(projectId, assetId));

    try {
        // Wait for the client ID to resolve to a server ID, in case this is a new asset.
        const outlookSpaceAssetId = await resolveAssetId(assetId);
        if (!outlookSpaceAssetId) {
            return;
        }

        // After the server ID is resolved, the instance might have been removed, otherwise, it should be the same.
        const assetAfterIdResolve = getAssetOrUndefined(projectId, assetId);

        // Either way, we'll verify if we can patch with the latest instance we have.
        const asset = assetAfterIdResolve ?? assetAtInvocation;
        if (!asset) {
            return;
        }

        // Ask the caller if we should still make the patch call.
        // For example, if the user moves the asset multiple times only the last "move" needs to be recorded.
        if (shouldPatch(asset)) {
            const { lastModifiedDateTime } = await patchOutlookSpaceAsset(
                projectId,
                outlookSpaceAssetId,
                changes
            );

            // use assetId instead of outlookSpaceAssetId because a new asset is still stored in the asset map under the local lie ID
            updateCachedAsset(projectId, assetId, { lastModifiedDateTime });
        }
    } catch (e) {
        // $TODO: handle Not Found errors to avoid the notification, probably delete asset

        // first check that the asset actually exists- if the asset has since been removed locally, no point in building a retry action
        if (!getAssetOrUndefined(projectId, assetId)) {
            return;
        }

        // if the asset still exists, but the update failed, undo the changes locally
        const newAsset = toJS(getAsset(projectId, assetId));
        const originalAsset = {
            ...newAsset,
            ...partialOriginalAsset,
        } as TimeStreamAsset;
        updateCachedAsset(projectId, assetId, originalAsset);

        showRetryNotification('patchAsset', loc(updateAssetFailed), () => {
            updateCachedAsset(projectId, assetId, newAsset);
            patchAsset(projectId, assetId, changes, originalAsset, shouldPatch);
        });
    }
}
